// Story 2.2, 2.6, 3.1, 3.2, 3.4, 3.5: Mileage Entry form modal (create + edit)
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, ChevronRight, AlertTriangle, MapPin, FileText } from 'lucide-react';
import { cn } from '@rhpc/ui';
import {
  useCreateMileage,
  useUpdateMileage,
  useMileageEntries,
  useMileageCalculate,
  useMileageRates,
  usePettyCashEntries,
  type CreateMileageInput,
  type MileageBreakdownPreview,
} from '../../hooks/useClaims';
import { MileageBreakdown, MileageBreakdownSkeleton } from './MileageBreakdown';
import { ApiError } from '../../lib/api';
import { LocationAutocomplete, saveRecentDestination } from './LocationAutocomplete';
import { AnimatedModal } from './AnimatedModal';
import { PettyCashFormModal } from './PettyCashFormModal';
import { useUserLocations } from '../../hooks/useUserLocations';
import { GoogleRouteMap } from './GoogleRouteMap';
import { RouteOptionCards } from './RouteOptionCards';
import type { RouteAlternative } from '../../lib/googleMaps';
import { findSavedRouteIndex } from '../../lib/savedRoute';

type MobileTab = 'details' | 'route';

function formatAmount(val: number): string {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(val);
}

interface MileageFormModalProps {
  claimId: string;
  entryId?: string;
  readOnly?: boolean;
  onClose: () => void;
}

function today(): string {
  return new Date().toISOString().split('T')[0]!;
}

type LocationType = 'HOME' | 'OFFICE' | 'OTHER';

const LOCATION_LABELS: Record<LocationType, string> = {
  HOME: 'Home',
  OFFICE: 'Office',
  OTHER: 'Other',
};

function formatPettyOption(entry: { date: string; description: string; amount: number | string }): string {
  const date = entry.date.split('T')[0] ?? entry.date;
  const amount = Number(entry.amount).toFixed(2);
  return `${date} — ${entry.description} (RM ${amount})`;
}

export function MileageFormModal({ claimId, entryId, readOnly = false, onClose }: MileageFormModalProps) {
  const isEditing = !!entryId;
  const createMileage = useCreateMileage(claimId);
  const updateMileage = useUpdateMileage(claimId);
  const calculate = useMileageCalculate();
  const { data: entriesData } = useMileageEntries(claimId);
  const { data: ratesData } = useMileageRates();
  const { data: pettyCashData } = usePettyCashEntries(claimId);
  const { data: userLocationsData } = useUserLocations();

  const userLocations = useMemo(() => userLocationsData?.locations ?? [], [userLocationsData]);
  const homeLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'HOME'), [userLocations]);
  const officeLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'OFFICE'), [userLocations]);
  const otherLocations = useMemo(() => userLocations.filter((l) => l.locationType === 'OTHER'), [userLocations]);

  const getSavedLocationsForType = useCallback(
    (type: LocationType) => {
      if (type === 'HOME') return homeLocations;
      if (type === 'OFFICE') return officeLocations;
      return otherLocations.length > 0 ? otherLocations : userLocations;
    },
    [homeLocations, officeLocations, otherLocations, userLocations],
  );

  const existingEntry = entriesData?.entries.find((e) => e.id === entryId);

  const [form, setForm] = useState<CreateMileageInput>({
    date: today(),
    origin: '',
    destination: '',
    purpose: '',
    vehicleType: 'Car',
    originType: 'OTHER',
    destType: 'OTHER',
    isRoundTrip: false,
    distanceOverride: undefined,
    overrideReason: '',
    parkingEntryId: null,
    tollEntryId: null,
    userLocationId: null,
  });

  const [originLocId, setOriginLocId] = useState<string>('');
  const [destLocId, setDestLocId] = useState<string>('');
  const [finalLocId, setFinalLocId] = useState<string>('');

  // Typing (or a Places suggestion) over an address that came from a saved-location
  // pick breaks the link: the saved id and its HOME/OFFICE type no longer describe
  // the text. Leave it alone when the text still equals a saved address, and when no
  // saved pick backs the endpoint (a type the user chose themselves is respected).
  const handleAddressTyped = (endpoint: 'origin' | 'dest' | 'final', val: string) => {
    const norm = (a: string) => a.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const addrKey = endpoint === 'origin' ? 'origin' : endpoint === 'dest' ? 'destination' : 'finalDestination';
    const typeKey = endpoint === 'origin' ? 'originType' : endpoint === 'dest' ? 'destType' : 'finalDestType';
    const picked = endpoint === 'origin' ? originLocId : endpoint === 'dest' ? destLocId : finalLocId;
    const stillSaved = !!norm(val) && userLocations.some((l) => norm(l.address) === norm(val));
    if (!picked || stillSaved) {
      setForm((f) => ({ ...f, [addrKey]: val }));
      return;
    }
    if (endpoint === 'origin') setOriginLocId('');
    else if (endpoint === 'dest') setDestLocId('');
    else setFinalLocId('');
    setForm((f) => {
      const next = { ...f, [addrKey]: val, [typeKey]: 'OTHER' as LocationType };
      if (f.userLocationId === picked) {
        // Another endpoint may still be the picked Home: origin, then dest, then final
        const remaining =
          endpoint !== 'origin' && f.originType === 'HOME' && originLocId ? originLocId
          : endpoint !== 'dest' && f.destType === 'HOME' && destLocId ? destLocId
          : endpoint !== 'final' && f.isRoundTrip && f.finalDestType === 'HOME' && finalLocId ? finalLocId
          : null;
        next.userLocationId = remaining;
      }
      return next;
    });
  };

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  // Below lg the two columns become tabs; desktop shows both side by side
  const [mobileTab, setMobileTab] = useState<MobileTab>('details');
  const detailsHasError = Boolean(errors['date'] || errors['overrideReason']);
  const routeHasError = Boolean(errors['origin'] || errors['destination'] || errors['finalDestination']);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MileageBreakdownPreview | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [viewingPettyCashEntryId, setViewingPettyCashEntryId] = useState<string | null>(null);

  // Route Alternatives (Google Maps)
  const [availableRoutes, setAvailableRoutes] = useState<RouteAlternative[]>([]);
  const [routesKey, setRoutesKey] = useState('');
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  // Return leg of a round trip: destination → final destination
  const [returnRoutes, setReturnRoutes] = useState<RouteAlternative[]>([]);
  const [returnRoutesKey, setReturnRoutesKey] = useState('');
  const [returnSelectedIndex, setReturnSelectedIndex] = useState(0);
  const [mapLeg, setMapLeg] = useState<'outbound' | 'return'>('outbound');
  const [isGoogleUnavailable, setIsGoogleUnavailable] = useState(false);
  // Override needs a calculated distance to override; when Google is down it is the only way to set one
  const canOverrideDistance =
    form.distanceOverride !== undefined || preview !== null || isGoogleUnavailable;

  // Only trust routes computed for the current addresses and vehicle
  const vehicleKey = (form.vehicleType ?? 'Car').trim().toLowerCase();
  const finalDestination = form.isRoundTrip ? (form.finalDestination ?? '') : '';
  const currentRouteKey = `${(form.origin ?? '').trim().toLowerCase()}|${(form.destination ?? '').trim().toLowerCase()}|${vehicleKey}`;
  const currentReturnKey = `${(form.destination ?? '').trim().toLowerCase()}|${finalDestination.trim().toLowerCase()}|${vehicleKey}`;
  // Keys of the saved entry's own inputs; routes computed for these should re-select the saved route
  const savedVehicleKey = (existingEntry?.vehicleType ?? 'Car').trim().toLowerCase();
  const savedFinalDestination = existingEntry?.isRoundTrip ? (existingEntry.finalDestination ?? existingEntry.origin) : '';
  const hasSavedRoute = !!existingEntry && existingEntry.distanceOverride == null;
  const savedRouteKey = hasSavedRoute
    ? `${existingEntry.origin.trim().toLowerCase()}|${existingEntry.destination.trim().toLowerCase()}|${savedVehicleKey}`
    : null;
  const savedReturnKey = hasSavedRoute
    ? `${existingEntry.destination.trim().toLowerCase()}|${savedFinalDestination.trim().toLowerCase()}|${savedVehicleKey}`
    : null;
  const activeRoute =
    routesKey === currentRouteKey && !isGoogleUnavailable ? availableRoutes[selectedRouteIndex] : undefined;
  const activeRouteKm = activeRoute?.distanceKm;
  const activeReturnRoute =
    form.isRoundTrip && returnRoutesKey === currentReturnKey && !isGoogleUnavailable
      ? returnRoutes[returnSelectedIndex]
      : undefined;
  const activeReturnKm = activeReturnRoute?.distanceKm;
  const shownMapLeg = form.isRoundTrip ? mapLeg : 'outbound';

  // Clear preview and routes when user edits From or To addresses
  const lastAddressPairRef = useRef<string>('');
  useEffect(() => {
    const pair = `${(form.origin ?? '').trim().toLowerCase()}|${(form.destination ?? '').trim().toLowerCase()}`;
    if (lastAddressPairRef.current && lastAddressPairRef.current !== pair) {
      setAvailableRoutes([]);
      setRoutesKey('');
      setPreview(null);
      setIsGoogleUnavailable(false);
    }
    lastAddressPairRef.current = pair;
  }, [form.origin, form.destination]);

  // Same for the return leg when To or the final destination changes
  const lastReturnPairRef = useRef<string>('');
  useEffect(() => {
    const pair = `${(form.destination ?? '').trim().toLowerCase()}|${(form.finalDestination ?? '').trim().toLowerCase()}`;
    if (lastReturnPairRef.current && lastReturnPairRef.current !== pair) {
      setReturnRoutes([]);
      setReturnRoutesKey('');
    }
    lastReturnPairRef.current = pair;
  }, [form.destination, form.finalDestination]);

  // Pre-fill when editing
  useEffect(() => {
    if (existingEntry) {
      setForm({
        date: existingEntry.date.split('T')[0]!,
        origin: existingEntry.origin,
        destination: existingEntry.destination,
        purpose: existingEntry.purpose ?? '',
        vehicleType: existingEntry.vehicleType ?? 'Car',
        originType: (existingEntry.originType as LocationType) ?? 'OTHER',
        destType: (existingEntry.destType as LocationType) ?? 'OTHER',
        isRoundTrip: existingEntry.isRoundTrip,
        finalDestination: existingEntry.finalDestination ?? (existingEntry.isRoundTrip ? existingEntry.origin : ''),
        finalDestType: (existingEntry.finalDestType as LocationType | null) ?? (existingEntry.originType as LocationType) ?? 'OTHER',
        distanceOverride: existingEntry.distanceOverride ? Number(existingEntry.distanceOverride) : undefined,
        overrideReason: existingEntry.overrideReason ?? '',
        parkingEntryId: existingEntry.parkingEntryId ?? null,
        tollEntryId: existingEntry.tollEntryId ?? null,
        userLocationId: existingEntry.userLocationId ?? null,
      });

      if (existingEntry.originType === 'HOME' && existingEntry.userLocationId) {
        setOriginLocId(existingEntry.userLocationId);
      }
      if (existingEntry.destType === 'HOME' && existingEntry.userLocationId) {
        setDestLocId(existingEntry.userLocationId);
      }
      if (
        existingEntry.isRoundTrip && existingEntry.finalDestType === 'HOME' && existingEntry.userLocationId
        && existingEntry.originType !== 'HOME' && existingEntry.destType !== 'HOME'
      ) {
        setFinalLocId(existingEntry.userLocationId);
      }
    }
  }, [existingEntry]);

  // Parking / Toll options: same-claim entries of that category, not linked to another mileage
  const linkedParkingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of entriesData?.entries ?? []) {
      if (m.parkingEntryId && m.id !== entryId) ids.add(m.parkingEntryId);
    }
    return ids;
  }, [entriesData?.entries, entryId]);

  const linkedTollIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of entriesData?.entries ?? []) {
      if (m.tollEntryId && m.id !== entryId) ids.add(m.tollEntryId);
    }
    return ids;
  }, [entriesData?.entries, entryId]);

  const parkingOptions = useMemo(
    () =>
      (pettyCashData?.entries ?? []).filter(
        (e) => e.category?.name === 'Parking' && !linkedParkingIds.has(e.id),
      ),
    [pettyCashData?.entries, linkedParkingIds],
  );

  const tollOptions = useMemo(
    () =>
      (pettyCashData?.entries ?? []).filter(
        (e) => e.category?.name === 'Toll' && !linkedTollIds.has(e.id),
      ),
    [pettyCashData?.entries, linkedTollIds],
  );

  // Live preview: trigger calculation when both addresses are present
  // outboundKm / returnKm: km of the routes picked on the map, if any
  const triggerPreview = useCallback(
    async (currentForm: typeof form, outboundKm?: number, returnKm?: number) => {
      if (!currentForm.origin?.trim() || !currentForm.destination?.trim()) {
        setPreview(null);
        return;
      }
      setIsCalculating(true);
      try {
        const hasOverride = currentForm.distanceOverride != null;
        const isRoundTrip = currentForm.isRoundTrip ?? false;
        const result = await calculate.mutateAsync({
          origin: currentForm.origin.trim(),
          destination: currentForm.destination.trim(),
          isRoundTrip,
          vehicleType: currentForm.vehicleType ?? 'Car',
          originType: currentForm.originType ?? 'OTHER',
          destType: currentForm.destType ?? 'OTHER',
          ...(isRoundTrip && {
            finalDestination: currentForm.finalDestination?.trim() || currentForm.origin.trim(),
            finalDestType: currentForm.finalDestType ?? 'OTHER',
          }),
          routeDistanceKm: hasOverride ? undefined : outboundKm,
          returnRouteDistanceKm: hasOverride || !isRoundTrip ? undefined : returnKm,
          distanceOverride: currentForm.distanceOverride ?? undefined,
          userLocationId: currentForm.userLocationId || undefined,
        });
        setPreview(result);
      } catch {
        setPreview(null);
      } finally {
        setIsCalculating(false);
      }
    },
    [calculate],
  );

  // User selects an alternative route (e.g. Route 2)
  const handleSelectRoute = useCallback(
    (index: number) => {
      setSelectedRouteIndex(index);
      const chosen = availableRoutes[index];
      if (chosen) {
        void triggerPreview(form, chosen.distanceKm, activeReturnKm);
      }
    },
    [availableRoutes, form, triggerPreview, activeReturnKm],
  );

  const handleSelectReturnRoute = useCallback(
    (index: number) => {
      setReturnSelectedIndex(index);
      const chosen = returnRoutes[index];
      if (chosen) {
        void triggerPreview(form, activeRouteKm, chosen.distanceKm);
      }
    },
    [returnRoutes, form, triggerPreview, activeRouteKm],
  );

  // Google Routes API returns route alternatives (Route 1 is auto-selected, unless reopening an entry saved with another route)
  const handleRoutesCalculated = useCallback(
    (newRoutes: RouteAlternative[]) => {
      setAvailableRoutes(newRoutes);
      setRoutesKey(newRoutes.length > 0 ? currentRouteKey : '');
      let index = 0;
      if (existingEntry && savedRouteKey === currentRouteKey) {
        const saved = findSavedRouteIndex(newRoutes, existingEntry.routeSummary, Number(existingEntry.distance));
        // Read-only: no match means nothing is shown as selected
        index = saved >= 0 ? saved : readOnly ? -1 : 0;
      }
      setSelectedRouteIndex(index);
      setIsGoogleUnavailable(false);
      const chosen = newRoutes[index];
      if (chosen) {
        void triggerPreview(form, chosen.distanceKm, activeReturnKm);
      }
    },
    [currentRouteKey, savedRouteKey, existingEntry, readOnly, form, triggerPreview, activeReturnKm],
  );

  const handleReturnRoutesCalculated = useCallback(
    (newRoutes: RouteAlternative[]) => {
      setReturnRoutes(newRoutes);
      setReturnRoutesKey(newRoutes.length > 0 ? currentReturnKey : '');
      let index = 0;
      if (existingEntry && savedReturnKey === currentReturnKey) {
        const saved = findSavedRouteIndex(newRoutes, existingEntry.returnRouteSummary, existingEntry.returnDistance != null ? Number(existingEntry.returnDistance) : null);
        index = saved >= 0 ? saved : readOnly ? -1 : 0;
      }
      setReturnSelectedIndex(index);
      const chosen = newRoutes[index];
      if (chosen) {
        void triggerPreview(form, activeRouteKm, chosen.distanceKm);
      }
    },
    [currentReturnKey, savedReturnKey, existingEntry, readOnly, form, triggerPreview, activeRouteKm],
  );

  // Fallback when Google API is down or quota is reached
  const handleGoogleApiUnavailable = useCallback(
    (_errMsg: string) => {
      setIsGoogleUnavailable(true);
      setAvailableRoutes([]);
      setRoutesKey('');
      setReturnRoutes([]);
      setReturnRoutesKey('');
      // Trigger fallback preview via OpenStreetMap
      void triggerPreview(form, undefined);
    },
    [form, triggerPreview],
  );

  // Update preview only when secondary options change (vehicle, round-trip, manual override)
  // Origin and Destination intentionally excluded: no calculation while typing!
  useEffect(() => {
    if (readOnly) return;
    if (form.distanceOverride != null) {
      void triggerPreview(form);
    } else if (activeRouteKm != null) {
      void triggerPreview(form, activeRouteKm, activeReturnKm);
    } else if (isGoogleUnavailable && preview) {
      void triggerPreview(form);
    }
  }, [readOnly, form.isRoundTrip, form.vehicleType, form.distanceOverride, form.originType, form.destType, form.finalDestType, form.userLocationId, activeRouteKm, activeReturnKm, isGoogleUnavailable]);

  const validate = (): typeof errors => {
    const errs: typeof errors = {};
    if (!form.date) errs['date'] = 'Date is required';
    if (form.date && new Date(form.date) > new Date()) errs['date'] = 'Date cannot be in the future';
    if (!form.origin?.trim()) errs['origin'] = 'Origin is required';
    if (!form.destination?.trim()) errs['destination'] = 'Destination is required';
    if (form.isRoundTrip && !form.finalDestination?.trim()) errs['finalDestination'] = 'Final destination is required';
    if (form.distanceOverride != null && !form.overrideReason?.trim()) {
      errs['overrideReason'] = 'Reason required when overriding distance';
    }
    setErrors(errs);
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      // Show the tab holding the first error (only matters on mobile)
      setMobileTab(errs['date'] || errs['overrideReason'] ? 'details' : 'route');
      return;
    }
    setSubmitError(null);

    // Picked routes go as route km (the server verifies them); only a typed
    // distance is an override. Editing without one clears an old override.
    const hasOverride = form.distanceOverride != null;

    try {
      const payload: CreateMileageInput = {
        date: form.date!,
        origin: form.origin!.trim(),
        destination: form.destination!.trim(),
        purpose: form.purpose?.trim() || undefined,
        vehicleType: form.vehicleType?.trim() || 'Car',
        originType: form.originType ?? 'OTHER',
        destType: form.destType ?? 'OTHER',
        isRoundTrip: form.isRoundTrip,
        ...(form.isRoundTrip && {
          finalDestination: form.finalDestination?.trim() || form.origin!.trim(),
          finalDestType: form.finalDestType ?? 'OTHER',
        }),
        routeDistanceKm: hasOverride ? undefined : activeRouteKm,
        returnRouteDistanceKm: hasOverride || !form.isRoundTrip ? undefined : activeReturnKm,
        distanceOverride: hasOverride ? form.distanceOverride : isEditing ? null : undefined,
        overrideReason: hasOverride ? form.overrideReason?.trim() || undefined : isEditing ? '' : undefined,
        parkingEntryId: form.parkingEntryId || null,
        tollEntryId: form.tollEntryId || null,
        userLocationId: form.userLocationId || null,
      };

      if (isEditing) {
        await updateMileage.mutateAsync({ entryId: entryId!, data: payload });
      } else {
        await createMileage.mutateAsync(payload);
      }
      saveRecentDestination(payload.origin);
      saveRecentDestination(payload.destination);
      if (payload.finalDestination) saveRecentDestination(payload.finalDestination);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('An error occurred. Please try again.');
      }
    }
  };

  const isLoading = createMileage.isPending || updateMileage.isPending;

  const vehicleTypes = ratesData ? Object.keys(ratesData.rates) : ['Car', 'Motorcycle', 'Other'];

  return (
    <>
      <AnimatedModal
        onClose={onClose}
        maxWidthClassName="max-w-[1040px]"
        footer={
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            {/* Compact total so the amount stays visible while picking a route (mobile only) */}
            <div className="flex min-h-5 items-center text-xs lg:hidden" aria-live="polite">
              {isCalculating ? (
                <span className="text-text-2">Calculating...</span>
              ) : preview ? (
                <span className="text-text-2">
                  Claimable <span className="font-mono text-text">{preview.breakdown.claimableDistanceKm.toFixed(2)} km</span>
                  {' · '}
                  <span className="font-mono font-semibold text-primary">{formatAmount(preview.breakdown.amountMyr)}</span>
                </span>
              ) : readOnly && existingEntry ? (
                <span className="text-text-2">
                  Claimable <span className="font-mono text-text">{Number(existingEntry.claimableDistance).toFixed(2)} km</span>
                  {' · '}
                  <span className="font-mono font-semibold text-primary">{formatAmount(Number(existingEntry.amount))}</span>
                </span>
              ) : mobileTab === 'details' ? (
                <button type="button" onClick={() => setMobileTab('route')} className="font-medium text-primary hover:underline">
                  Pick a route to calculate →
                </button>
              ) : (
                <span className="text-text-2">Enter From and To, then get the route.</span>
              )}
            </div>

            <div className="flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-md border border-border px-[18px] text-sm font-medium transition-colors hover:bg-surface-2"
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button
                  id="mileage-submit-btn"
                  type="submit"
                  form="mileage-entry-form"
                  disabled={isLoading}
                  className="h-10 rounded-md bg-primary px-[18px] text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
                >
                  {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Entry'}
                </button>
              )}
            </div>
            {!readOnly && (
              <p className="hidden text-xs text-text-2 lg:order-first lg:block">
                Rate is calculated automatically from your vehicle and distance.
              </p>
            )}
          </div>
        }
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold">
            {readOnly ? 'Mileage Entry Details' : isEditing ? 'Edit Mileage Entry' : 'Add Mileage'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        {/* Mobile tab switcher (hidden on desktop, where both columns show) */}
        <div role="tablist" aria-label="Mileage entry sections" className="mb-4 grid grid-cols-2 gap-0.5 rounded-lg bg-surface-2 p-[3px] lg:hidden">
          {([
            { id: 'details', label: 'Details', Icon: FileText, hasError: detailsHasError },
            { id: 'route', label: 'Route', Icon: MapPin, hasError: routeHasError },
          ] as const).map(({ id, label, Icon, hasError }) => (
            <button
              key={id}
              id={`mileage-tab-${id}`}
              type="button"
              role="tab"
              aria-selected={mobileTab === id}
              aria-controls={`mileage-panel-${id}`}
              onClick={() => setMobileTab(id)}
              className={cn(
                'flex h-9 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition-colors',
                mobileTab === id
                  ? 'bg-surface text-text shadow-[0_1px_2px_0_rgb(15_23_42_/_0.08)]'
                  : 'text-text-2 hover:text-text'
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {label}
              {hasError && (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-danger" aria-hidden />
                  <span className="sr-only">(has errors)</span>
                </>
              )}
            </button>
          ))}
        </div>

        <form id="mileage-entry-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {submitError && (
            <div className="rounded-md border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* ============================================================ */}
            {/* LEFT COLUMN: Claim Details, Overrides, Breakdown & Actions */}
            {/* ============================================================ */}
            <div
              id="mileage-panel-details"
              className={cn(mobileTab === 'details' ? 'flex' : 'hidden', 'flex-col gap-4 lg:flex')}
            >
              {/* Date */}
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-date">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  id="mileage-date"
                  type="date"
                  max={today()}
                  value={form.date}
                  readOnly={readOnly}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                />
                {errors['date'] && <p className="mt-1 text-xs text-danger">{errors['date']}</p>}
              </div>

              {/* Purpose */}
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-purpose">
                  Purpose
                </label>
                <input
                  id="mileage-purpose"
                  type="text"
                  placeholder="e.g. Client meeting"
                  value={form.purpose}
                  readOnly={readOnly}
                  onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
                  className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                />
              </div>

              {/* Optional Parking / Toll links */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-parking">
                    Linked Parking <span className="font-normal text-text-2">(optional)</span>
                  </label>
                  {readOnly ? (
                    <button
                      id="mileage-parking"
                      type="button"
                      disabled={!form.parkingEntryId}
                      onClick={() => form.parkingEntryId && setViewingPettyCashEntryId(form.parkingEntryId)}
                      className="flex h-10 w-full items-center rounded-md border border-border bg-surface-2/30 px-3 text-left text-sm enabled:cursor-pointer enabled:hover:bg-surface-2 disabled:cursor-default"
                      aria-label={form.parkingEntryId ? 'View linked parking entry' : 'No linked parking entry'}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {parkingOptions.find((entry) => entry.id === form.parkingEntryId)
                          ? formatPettyOption(parkingOptions.find((entry) => entry.id === form.parkingEntryId)!)
                          : 'None'}
                      </span>
                      {form.parkingEntryId && <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-text-2" />}
                    </button>
                  ) : (
                    <select
                      id="mileage-parking"
                      value={form.parkingEntryId ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, parkingEntryId: e.target.value || null }))}
                      className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                    >
                      <option value="">None</option>
                      {parkingOptions.map((e) => (
                        <option key={e.id} value={e.id}>{formatPettyOption(e)}</option>
                      ))}
                    </select>
                  )}
                  {parkingOptions.length === 0 && (
                    <p className="mt-1 text-[11px] text-text-2">
                      Add a Parking petty cash entry on this claim first.
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-toll">
                    Linked Toll <span className="font-normal text-text-2">(optional)</span>
                  </label>
                  {readOnly ? (
                    <button
                      id="mileage-toll"
                      type="button"
                      disabled={!form.tollEntryId}
                      onClick={() => form.tollEntryId && setViewingPettyCashEntryId(form.tollEntryId)}
                      className="flex h-10 w-full items-center rounded-md border border-border bg-surface-2/30 px-3 text-left text-sm enabled:cursor-pointer enabled:hover:bg-surface-2 disabled:cursor-default"
                      aria-label={form.tollEntryId ? 'View linked toll entry' : 'No linked toll entry'}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {tollOptions.find((entry) => entry.id === form.tollEntryId)
                          ? formatPettyOption(tollOptions.find((entry) => entry.id === form.tollEntryId)!)
                          : 'None'}
                      </span>
                      {form.tollEntryId && <ChevronRight className="ml-2 h-4 w-4 shrink-0 text-text-2" />}
                    </button>
                  ) : (
                    <select
                      id="mileage-toll"
                      value={form.tollEntryId ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, tollEntryId: e.target.value || null }))}
                      className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                    >
                      <option value="">None</option>
                      {tollOptions.map((e) => (
                        <option key={e.id} value={e.id}>{formatPettyOption(e)}</option>
                      ))}
                    </select>
                  )}
                  {tollOptions.length === 0 && (
                    <p className="mt-1 text-[11px] text-text-2">
                      Add a Toll petty cash entry on this claim first.
                    </p>
                  )}
                </div>
              </div>

              {/* Live calculation preview */}
              {readOnly && existingEntry ? (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-text-2">Calculation Breakdown</p>
                  <MileageBreakdown
                    origin={existingEntry.origin}
                    destination={existingEntry.destination}
                    finalDestination={existingEntry.isRoundTrip ? existingEntry.finalDestination ?? existingEntry.origin : null}
                    vehicleType={existingEntry.vehicleType}
                    breakdown={{
                      // Override is the whole-trip total; otherwise outbound + return legs
                      totalDistanceKm: existingEntry.distanceOverride != null
                        ? Number(existingEntry.distanceOverride)
                        : Number(existingEntry.distance) + Number(existingEntry.returnDistance ?? 0),
                      homeOfficeDeductionKm: Number(existingEntry.homeOfficeDeduction),
                      claimableDistanceKm: Number(existingEntry.claimableDistance),
                      ratePerKm: Number(existingEntry.rateUsed ?? 0),
                      amountMyr: Number(existingEntry.amount),
                      deductionApplied: Number(existingEntry.homeOfficeDeduction) > 0,
                      deductionExplanation: 'Home-office travel deduction applied.',
                    }}
                    hasOverride={existingEntry.distanceOverride != null}
                    overrideReason={existingEntry.overrideReason}
                  />
                </div>
              ) : (form.origin?.trim() && form.destination?.trim()) && (
                <div>
                  <p className="mb-1.5 text-xs font-medium text-text-2">Calculation Preview</p>
                  {isCalculating ? (
                    <MileageBreakdownSkeleton
                      message={
                        isGoogleUnavailable
                          ? 'Calculating distance via OpenStreetMap...'
                          : 'Calculating reimbursement...'
                      }
                    />
                  ) : preview ? (
                    <MileageBreakdown
                      origin={preview.resolvedOrigin}
                      destination={preview.resolvedDestination}
                      finalDestination={preview.resolvedFinalDestination ?? null}
                      vehicleType={preview.vehicleType}
                      breakdown={preview.breakdown}
                      hasOverride={!!form.distanceOverride}
                      overrideReason={form.overrideReason}
                    />
                  ) : (
                    <div className="rounded-lg border border-dashed border-border bg-surface-2/30 p-4 text-center">
                      <p className="text-xs text-text-2">
                        Click <span className="font-semibold text-primary">Get Route</span> to select your route and calculate reimbursement.
                      </p>
                    </div>
                  )}
                  {!isCalculating && preview && form.distanceOverride === undefined && (
                    activeRoute ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-success">
                        <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                        Distance via Google Maps · {form.isRoundTrip
                          ? `Outbound route ${selectedRouteIndex + 1}, return route ${activeReturnRoute ? returnSelectedIndex + 1 : 1}`
                          : `Route ${selectedRouteIndex + 1}`}
                      </p>
                    ) : isGoogleUnavailable || preview.distanceSource === 'osm' ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-warn">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                        Estimate via OpenStreetMap (Google Maps unavailable)
                      </p>
                    ) : preview.distanceSource === 'google' ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-success">
                        <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                        Distance via Google Maps
                      </p>
                    ) : null
                  )}
                  {preview && !preview.mapsAvailable && !activeRoute && form.distanceOverride === undefined && !isCalculating && !isGoogleUnavailable && (
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-warn">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                      Auto-distance unavailable. Enter a manual distance below, or save to use the configured default.
                    </p>
                  )}
                </div>
              )}

              {/* Distance Override */}
              {canOverrideDistance && (
                <details className="group" open={readOnly && form.distanceOverride !== undefined ? true : undefined}>
                  <summary className="flex list-none cursor-pointer items-center gap-1 text-[13px] font-semibold text-text">
                    <ChevronRight
                      className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90 text-text-2"
                      strokeWidth={2.5}
                    />
                    Override distance manually
                  </summary>
                  <div className="mt-2.5 space-y-2">
                    <input
                      id="mileage-distance"
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="Distance in km"
                      value={form.distanceOverride ?? ''}
                      readOnly={readOnly}
                      onChange={(e) => setForm((f) => ({ ...f, distanceOverride: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      className="h-10 w-full rounded-md border border-input bg-surface px-3 font-mono text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                    />
                    {form.distanceOverride !== undefined && (
                      <div>
                        <input
                          id="mileage-override-reason"
                          type="text"
                          placeholder="Reason for manual distance (required)"
                          value={form.overrideReason}
                          readOnly={readOnly}
                          onChange={(e) => setForm((f) => ({ ...f, overrideReason: e.target.value }))}
                          className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-text-2 focus:outline-2 focus:outline-primary focus:outline-offset-1"
                        />
                        {errors['overrideReason'] && <p className="mt-1 text-xs text-danger">{errors['overrideReason']}</p>}
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>

            {/* ============================================================ */}
            {/* RIGHT COLUMN: From, To, Google Route Map & Alternatives     */}
            {/* ============================================================ */}
            <div
              id="mileage-panel-route"
              className={cn(mobileTab === 'route' ? 'flex' : 'hidden', 'flex-col gap-4 lg:flex')}
            >
              {/* Origin + location type */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[13px] font-semibold" htmlFor="mileage-origin">
                    From <span className="text-danger">*</span>
                  </label>
                  {(() => {
                    const locs = getSavedLocationsForType(form.originType || 'OTHER');
                    if (locs.length === 0 || readOnly) return null;
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-text-2">
                        <span>{form.originType === 'HOME' ? 'Base:' : 'Saved:'}</span>
                        <select
                          value={originLocId}
                          onChange={(e) => {
                            const locId = e.target.value;
                            setOriginLocId(locId);
                            const selectedLoc = locs.find((l) => l.id === locId);
                            if (selectedLoc) {
                              setForm((f) => ({
                                ...f,
                                origin: selectedLoc.address,
                                originType: selectedLoc.locationType as LocationType,
                                userLocationId: selectedLoc.locationType === 'HOME' ? selectedLoc.id : f.userLocationId,
                              }));
                            }
                          }}
                          className="max-w-[170px] truncate rounded border border-input bg-surface px-1.5 py-0.5 text-xs focus:ring-1 focus:outline-primary"
                        >
                          <option value="">Choose saved location...</option>
                          {locs.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.label} {loc.isDefault ? '(Default)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-[112px_1fr] gap-2">
                  <select
                    id="mileage-origin-type"
                    value={form.originType}
                    disabled={readOnly}
                    onChange={(e) => {
                      const newType = e.target.value as LocationType;
                      let newOrigin = form.origin;
                      let newLocId = form.userLocationId;
                      let newOriginLocId = '';

                      if (newType === 'HOME') {
                        const defaultHome = homeLocations.find((l) => l.isDefault) || homeLocations[0];
                        if (defaultHome) {
                          newOrigin = defaultHome.address;
                          newLocId = defaultHome.id;
                          newOriginLocId = defaultHome.id;
                        }
                      } else if (newType === 'OFFICE') {
                        const defaultOffice = officeLocations.find((l) => l.isDefault) || officeLocations[0];
                        if (defaultOffice) {
                          newOrigin = defaultOffice.address;
                          newOriginLocId = defaultOffice.id;
                        }
                      } else if (newType === 'OTHER') {
                        const defaultOther = otherLocations.find((l) => l.isDefault) || otherLocations[0];
                        if (defaultOther) {
                          newOrigin = defaultOther.address;
                          newOriginLocId = defaultOther.id;
                        }
                      }

                      setOriginLocId(newOriginLocId);
                      setForm((f) => ({
                        ...f,
                        originType: newType,
                        origin: newOrigin || '',
                        userLocationId: newLocId,
                      }));
                    }}
                    className="h-10 rounded-md border border-input bg-surface px-2 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                  >
                    {(Object.keys(LOCATION_LABELS) as LocationType[]).map((lt) => (
                      <option key={lt} value={lt}>{LOCATION_LABELS[lt]}</option>
                    ))}
                  </select>
                  {readOnly ? (
                    <input
                      id="mileage-origin"
                      value={form.origin || ''}
                      readOnly
                      className="h-10 w-full rounded-md border border-input bg-surface-2/30 px-3 text-sm"
                    />
                  ) : (
                    <LocationAutocomplete
                      id="mileage-origin"
                      placeholder="Address or place name"
                      value={form.origin || ''}
                      onChange={(val) => handleAddressTyped('origin', val)}
                      className="w-full"
                    />
                  )}
                </div>
                {errors['origin'] && <p className="mt-1 text-xs text-danger">{errors['origin']}</p>}
              </div>

              {/* Destination + location type */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[13px] font-semibold" htmlFor="mileage-dest">
                    To <span className="text-danger">*</span>
                  </label>
                  {(() => {
                    const locs = getSavedLocationsForType(form.destType || 'OTHER');
                    if (locs.length === 0 || readOnly) return null;
                    return (
                      <div className="flex items-center gap-1.5 text-xs text-text-2">
                        <span>{form.destType === 'HOME' ? 'Base:' : 'Saved:'}</span>
                        <select
                          value={destLocId}
                          onChange={(e) => {
                            const locId = e.target.value;
                            setDestLocId(locId);
                            const selectedLoc = locs.find((l) => l.id === locId);
                            if (selectedLoc) {
                              setForm((f) => ({
                                ...f,
                                destination: selectedLoc.address,
                                destType: selectedLoc.locationType as LocationType,
                                userLocationId: (selectedLoc.locationType === 'HOME' && f.originType !== 'HOME') ? selectedLoc.id : f.userLocationId,
                              }));
                            }
                          }}
                          className="max-w-[170px] truncate rounded border border-input bg-surface px-1.5 py-0.5 text-xs focus:ring-1 focus:outline-primary"
                        >
                          <option value="">Choose saved location...</option>
                          {locs.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.label} {loc.isDefault ? '(Default)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-[112px_1fr] gap-2">
                  <select
                    id="mileage-dest-type"
                    value={form.destType}
                    disabled={readOnly}
                    onChange={(e) => {
                      const newType = e.target.value as LocationType;
                      let newDest = form.destination;
                      let newLocId = form.userLocationId;
                      let newDestLocId = '';

                      if (newType === 'HOME') {
                        const defaultHome = homeLocations.find((l) => l.isDefault) || homeLocations[0];
                        if (defaultHome) {
                          newDest = defaultHome.address;
                          newDestLocId = defaultHome.id;
                          if (form.originType !== 'HOME') {
                            newLocId = defaultHome.id;
                          }
                        }
                      } else if (newType === 'OFFICE') {
                        const defaultOffice = officeLocations.find((l) => l.isDefault) || officeLocations[0];
                        if (defaultOffice) {
                          newDest = defaultOffice.address;
                          newDestLocId = defaultOffice.id;
                        }
                      } else if (newType === 'OTHER') {
                        const defaultOther = otherLocations.find((l) => l.isDefault) || otherLocations[0];
                        if (defaultOther) {
                          newDest = defaultOther.address;
                          newDestLocId = defaultOther.id;
                        }
                      }

                      setDestLocId(newDestLocId);
                      setForm((f) => ({
                        ...f,
                        destType: newType,
                        destination: newDest || '',
                        userLocationId: newLocId,
                      }));
                    }}
                    className="h-10 rounded-md border border-input bg-surface px-2 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                  >
                    {(Object.keys(LOCATION_LABELS) as LocationType[]).map((lt) => (
                      <option key={lt} value={lt}>{LOCATION_LABELS[lt]}</option>
                    ))}
                  </select>
                  {readOnly ? (
                    <input
                      id="mileage-dest"
                      value={form.destination || ''}
                      readOnly
                      className="h-10 w-full rounded-md border border-input bg-surface-2/30 px-3 text-sm"
                    />
                  ) : (
                    <LocationAutocomplete
                      id="mileage-dest"
                      placeholder="Address or place name"
                      value={form.destination || ''}
                      onChange={(val) => handleAddressTyped('dest', val)}
                      className="w-full"
                    />
                  )}
                </div>
                {errors['destination'] && <p className="mt-1 text-xs text-danger">{errors['destination']}</p>}
              </div>

              {/* Vehicle + Round Trip (vehicle decides car vs motorcycle routing) */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="mb-1.5 block text-[13px] font-semibold" htmlFor="mileage-vehicle">
                    Vehicle
                  </label>
                  <select
                    id="mileage-vehicle"
                    value={form.vehicleType}
                    disabled={readOnly}
                    onChange={(e) => setForm((f) => ({ ...f, vehicleType: e.target.value }))}
                    className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                  >
                    {vehicleTypes.map((vt) => (
                      <option key={vt} value={vt}>
                        {vt} {ratesData?.rates[vt] != null ? `(RM ${ratesData.rates[vt]?.toFixed(2)}/km)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm mt-5">
                  <input
                    id="mileage-round-trip"
                    type="checkbox"
                    checked={form.isRoundTrip}
                    disabled={readOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      // A round trip ends back at From unless the user picks somewhere else
                      setForm((f) => ({
                        ...f,
                        isRoundTrip: checked,
                        ...(checked && !f.finalDestination?.trim() && {
                          finalDestination: f.origin ?? '',
                          finalDestType: f.originType ?? 'OTHER',
                        }),
                      }));
                      if (!checked) setMapLeg('outbound');
                    }}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  Round trip
                </label>
              </div>

              {/* Final destination (round trip only): where the trip ends, From by default */}
              {form.isRoundTrip && (
                <div>
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <label className="text-[13px] font-semibold" htmlFor="mileage-final">
                      Final destination <span className="text-danger">*</span>
                    </label>
                    {!readOnly && (
                      <div className="flex items-center gap-3 text-xs text-text-2">
                        {(form.finalDestination ?? '').trim() !== (form.origin ?? '').trim() && (form.origin ?? '').trim() && (
                          <button
                            type="button"
                            onClick={() => {
                              setFinalLocId('');
                              setForm((f) => ({ ...f, finalDestination: f.origin ?? '', finalDestType: f.originType ?? 'OTHER' }));
                            }}
                            className="font-medium text-primary hover:underline"
                          >
                            Same as From
                          </button>
                        )}
                        {(() => {
                          const locs = getSavedLocationsForType(form.finalDestType || 'OTHER');
                          if (locs.length === 0) return null;
                          return (
                            <span className="flex items-center gap-1.5">
                              <span>Saved:</span>
                              <select
                                value={finalLocId}
                                aria-label="Saved location for final destination"
                                onChange={(e) => {
                                  const selectedLoc = locs.find((l) => l.id === e.target.value);
                                  setFinalLocId(e.target.value);
                                  if (selectedLoc) {
                                    setForm((f) => ({
                                      ...f,
                                      finalDestination: selectedLoc.address,
                                      finalDestType: selectedLoc.locationType as LocationType,
                                      userLocationId:
                                        selectedLoc.locationType === 'HOME' && f.originType !== 'HOME' && f.destType !== 'HOME'
                                          ? selectedLoc.id
                                          : f.userLocationId,
                                    }));
                                  }
                                }}
                                className="max-w-[170px] truncate rounded border border-input bg-surface px-1.5 py-0.5 text-xs focus:ring-1 focus:outline-primary"
                              >
                                <option value="">Choose saved location...</option>
                                {locs.map((loc) => (
                                  <option key={loc.id} value={loc.id}>
                                    {loc.label} {loc.isDefault ? '(Default)' : ''}
                                  </option>
                                ))}
                              </select>
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-[112px_1fr] gap-2">
                    <select
                      id="mileage-final-type"
                      aria-label="Final destination type"
                      value={form.finalDestType ?? 'OTHER'}
                      disabled={readOnly}
                      onChange={(e) => {
                        const newType = e.target.value as LocationType;
                        const pool = newType === 'HOME' ? homeLocations : newType === 'OFFICE' ? officeLocations : otherLocations;
                        const preset = pool.find((l) => l.isDefault) || pool[0];
                        setFinalLocId(preset?.id ?? '');
                        setForm((f) => ({
                          ...f,
                          finalDestType: newType,
                          finalDestination: preset?.address ?? f.finalDestination ?? '',
                          userLocationId:
                            preset && newType === 'HOME' && f.originType !== 'HOME' && f.destType !== 'HOME'
                              ? preset.id
                              : f.userLocationId,
                        }));
                      }}
                      className="h-10 rounded-md border border-input bg-surface px-2 text-sm focus:outline-2 focus:outline-primary focus:outline-offset-1"
                    >
                      {(Object.keys(LOCATION_LABELS) as LocationType[]).map((lt) => (
                        <option key={lt} value={lt}>{LOCATION_LABELS[lt]}</option>
                      ))}
                    </select>
                    {readOnly ? (
                      <input
                        id="mileage-final"
                        value={form.finalDestination || ''}
                        readOnly
                        className="h-10 w-full rounded-md border border-input bg-surface-2/30 px-3 text-sm"
                      />
                    ) : (
                      <LocationAutocomplete
                        id="mileage-final"
                        placeholder="Address or place name"
                        value={form.finalDestination || ''}
                        onChange={(val) => handleAddressTyped('final', val)}
                        className="w-full"
                      />
                    )}
                  </div>
                  {errors['finalDestination'] && <p className="mt-1 text-xs text-danger">{errors['finalDestination']}</p>}
                </div>
              )}

              {/* Outbound / Return tabs: each leg has its own map and route choice */}
              {form.isRoundTrip && (
                <div role="tablist" aria-label="Trip legs" className="grid grid-cols-2 gap-0.5 rounded-lg bg-surface-2 p-[3px]">
                  {([
                    { id: 'outbound', label: 'Outbound', km: activeRouteKm },
                    { id: 'return', label: 'Return', km: activeReturnKm },
                  ] as const).map(({ id, label, km }) => (
                    <button
                      key={id}
                      id={`mileage-leg-tab-${id}`}
                      type="button"
                      role="tab"
                      aria-selected={shownMapLeg === id}
                      aria-controls={`mileage-leg-panel-${id}`}
                      onClick={() => setMapLeg(id)}
                      className={cn(
                        'flex h-9 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition-colors',
                        shownMapLeg === id
                          ? 'bg-surface text-text shadow-[0_1px_2px_0_rgb(15_23_42_/_0.08)]'
                          : 'text-text-2 hover:text-text'
                      )}
                    >
                      {label}
                      {km != null && <span className="font-mono text-xs font-normal text-text-2">{km.toFixed(1)} km</span>}
                    </button>
                  ))}
                </div>
              )}

              {/* Outbound leg: From → To */}
              <div id="mileage-leg-panel-outbound" className={shownMapLeg === 'outbound' ? 'flex flex-col gap-4' : 'hidden'}>
                <GoogleRouteMap
                  origin={form.origin || ''}
                  destination={form.destination || ''}
                  vehicleType={form.vehicleType || 'Car'}
                  selectedRouteIndex={selectedRouteIndex}
                  onSelectRouteIndex={handleSelectRoute}
                  onRoutesCalculated={handleRoutesCalculated}
                  onGoogleApiUnavailable={handleGoogleApiUnavailable}
                />
                <RouteOptionCards
                  routes={routesKey === currentRouteKey && !isGoogleUnavailable ? availableRoutes : []}
                  selectedIndex={selectedRouteIndex}
                  onSelect={handleSelectRoute}
                  disabled={readOnly}
                />
              </div>

              {/* Return leg: To → Final destination. Stays mounted so its map and choice survive tab switches */}
              {form.isRoundTrip && (
                <div id="mileage-leg-panel-return" className={shownMapLeg === 'return' ? 'flex flex-col gap-4' : 'hidden'}>
                  <GoogleRouteMap
                    origin={form.destination || ''}
                    destination={finalDestination}
                    vehicleType={form.vehicleType || 'Car'}
                    selectedRouteIndex={returnSelectedIndex}
                    onSelectRouteIndex={handleSelectReturnRoute}
                    onRoutesCalculated={handleReturnRoutesCalculated}
                    onGoogleApiUnavailable={handleGoogleApiUnavailable}
                  />
                  <RouteOptionCards
                    routes={returnRoutesKey === currentReturnKey && !isGoogleUnavailable ? returnRoutes : []}
                    selectedIndex={returnSelectedIndex}
                    onSelect={handleSelectReturnRoute}
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </AnimatedModal>
      {viewingPettyCashEntryId && (
        <PettyCashFormModal
          claimId={claimId}
          entryId={viewingPettyCashEntryId}
          readOnly
          onClose={() => setViewingPettyCashEntryId(null)}
        />
      )}
    </>
  );
}
